import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { api, errMsg } from "../api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../components/ui/select";
import { Plus, Trash2, FileText } from "lucide-react";

export default function PayCalculator() {
  const nav = useNavigate();
  const [drivers, setDrivers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [driverId, setDriverId] = useState("");
  const [payDate, setPayDate] = useState(new Date().toISOString().slice(0, 10));
  const [periodStart, setPeriodStart] = useState("");
  const [periodEnd, setPeriodEnd] = useState("");
  const [periodPreset, setPeriodPreset] = useState("custom");
  const [trips, setTrips] = useState([]);
  const [selectedTripIds, setSelectedTripIds] = useState([]);
  const [extraLines, setExtraLines] = useState([]); // {label, amount, kind: deduction|reimbursement|bonus|flat}
  const [weeklyLines, setWeeklyLines] = useState([]); // recurring per-period items (applied ONCE)
  const [notes, setNotes] = useState("");
  // Dispatcher message printed on every statement generated from this pay run
  // (PDF + on-screen). The same text can be reused across pay runs for
  // fleet-wide announcements.
  const [message, setMessage] = useState("");
  // "driver" (only this statement) | "team" (only teams -- hidden on single
  // driver stubs) | "all" (fleet-wide: shows on every statement).
  const [messageAudience, setMessageAudience] = useState("driver");
  // Pay-run-level override: driver's % share applied to every trip in THIS pay
  // run (percentage drivers only). Defaults to the driver's stored pay_rate
  // when a driver is picked, but the user can override per pay run.
  const [payRunDriverPct, setPayRunDriverPct] = useState("");
  // Back office % for this pay run -- INDEPENDENT of the driver %. Blank = use each trip's / driver's own value.
  const [payRunBoPct, setPayRunBoPct] = useState("");
  // Line haul rate ($/mi) for this pay run. Defaults to the driver's stored
  // base_rate_per_mile; the backend persists this back to the driver on save.
  const [payRunBaseRate, setPayRunBaseRate] = useState("");
  // Company-wide default driver's share %. The Pay Run page seeds its %
  // from this on load, and the "Save as company default" button writes it
  // back here + bulk-updates every percentage driver.
  // Default to 50 (common 50/50 split) rather than 65; the server value from
  // /settings overrides this once loaded.
  const [companyDefaultPct, setCompanyDefaultPct] = useState(50);
  const [applyingDefault, setApplyingDefault] = useState(false);

  useEffect(() => {
    api.get("/drivers").then((r) => setDrivers(r.data)).catch(() => {});
    api.get("/deduction-templates").then((r) => setTemplates(r.data)).catch(() => {});
    api.get("/settings").then((r) => {
      const p = Number(r.data?.default_driver_pct);
      if (!Number.isNaN(p) && p > 0) {
        setCompanyDefaultPct(p);
        setPayRunDriverPct((prev) => (prev === "" ? String(p) : prev));
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!driverId) { setTrips([]); setSelectedTripIds([]); setWeeklyLines([]); return; }
    api.get("/trips", { params: { driver_id: driverId } }).then((r) => {
      setTrips(r.data);
      setSelectedTripIds([]);
    }).catch(() => {});
    // Pre-load the driver's recurring weekly deductions so they're applied
    // ONCE per statement regardless of how many trips are in the period.
    const d = drivers.find((x) => x.driver_id === driverId);
    const wk = (d && d.weekly_deductions) || [];
    setWeeklyLines(wk.map((w) => ({
      label: w.label || "",
      amount: parseFloat(w.amount) || 0,
      kind: w.kind || "deduction",
      note: w.note || "",
      enabled: true,
    })));
    // Only seed the pay-run % if the user hasn't picked one yet. The company
    // default drives the field; don't overwrite it when a driver is picked --
    // that caused per-driver stored % to shadow the dispatcher's default.
    if (d && d.pay_type === "per_percentage") {
      setPayRunDriverPct((prev) => (prev === "" ? String(companyDefaultPct) : prev));
      // Pre-fill line-haul rate from this driver's last-saved base rate.
      setPayRunBaseRate(d.base_rate_per_mile != null ? String(d.base_rate_per_mile) : "");
    } else if (!d || d.pay_type !== "per_percentage") {
      // Per-mile driver -- % is irrelevant. Keep the current value so switching
      // back to a percentage driver keeps whatever the dispatcher had typed.
      setPayRunBaseRate("");
    }
  }, [driverId, drivers, companyDefaultPct]);

  const applyAsCompanyDefault = async () => {
    const pct = parseFloat(payRunDriverPct);
    if (Number.isNaN(pct) || pct <= 0 || pct >= 100) {
      toast.error("Enter a driver's % between 1 and 99");
      return;
    }
    if (!window.confirm(
      `Set ${pct}% as the company-wide default driver's share?\n\n`
      + `This updates EVERY existing percentage driver (pay rate + back office split) `
      + `and seeds every new driver with this value. New trips will use ${pct}% too.`
    )) return;
    setApplyingDefault(true);
    try {
      const bo = Math.max(0, 100 - pct);
      // Push BOTH driver % and complementary company % so the saved default
      // round-trips cleanly next week. The backend endpoint persists these
      // to /settings AND bulk-updates every percentage driver's pay_rate
      // and default_back_office_percent.
      const r = await api.post("/settings/apply-default-driver-pct", { pct, back_office_pct: bo });
      setCompanyDefaultPct(pct);
      setPayRunBoPct(String(bo));
      // Refresh the local driver list so the pill next to the picker reflects
      // the new stored rate immediately (every driver's pay_rate now = pct).
      const dr = await api.get("/drivers");
      setDrivers(dr.data);
      toast.success(`Default set to ${pct}% / ${bo}% · ${r.data.updated_drivers} driver(s) updated`);
    } catch (e) {
      toast.error(errMsg(e, "Failed to apply default"));
    } finally {
      setApplyingDefault(false);
    }
  };

  const addWeekly = () =>
    setWeeklyLines((p) => [...p, { _rid: crypto.randomUUID(), label: "", amount: "", kind: "deduction", note: "", enabled: true }]);
  const updWeekly = (i, k, v) =>
    setWeeklyLines((p) => { const n = [...p]; n[i] = { ...n[i], [k]: k === "amount" ? (parseFloat(v) || 0) : v }; return n; });
  const rmWeekly = (i) => setWeeklyLines((p) => p.filter((_, idx) => idx !== i));

  const applyPreset = (preset) => {
    setPeriodPreset(preset);
    const now = new Date();
    const iso = (d) => d.toISOString().slice(0, 10);
    const day = now.getDay();
    const monday = new Date(now); monday.setDate(now.getDate() - ((day + 6) % 7));
    const sunday = new Date(monday); sunday.setDate(monday.getDate() + 6);
    if (preset === "this_week") { setPeriodStart(iso(monday)); setPeriodEnd(iso(sunday)); }
    else if (preset === "last_week") {
      const ls = new Date(monday); ls.setDate(ls.getDate() - 7);
      const le = new Date(sunday); le.setDate(le.getDate() - 7);
      setPeriodStart(iso(ls)); setPeriodEnd(iso(le));
    }
    else if (preset === "this_month") {
      setPeriodStart(iso(new Date(now.getFullYear(), now.getMonth(), 1)));
      setPeriodEnd(iso(new Date(now.getFullYear(), now.getMonth() + 1, 0)));
    }
    else if (preset === "last_month") {
      setPeriodStart(iso(new Date(now.getFullYear(), now.getMonth() - 1, 1)));
      setPeriodEnd(iso(new Date(now.getFullYear(), now.getMonth(), 0)));
    }
  };

  const driver = useMemo(() => drivers.find((d) => d.driver_id === driverId), [drivers, driverId]);
  const inRangeTrips = useMemo(() => {
    if (!periodStart || !periodEnd) return trips;
    return trips.filter((t) => t.load_date >= periodStart && t.load_date <= periodEnd);
  }, [trips, periodStart, periodEnd]);

  const pullTrips = () => {
    if (!driver) { toast.error("Pick a driver"); return; }
    if (!periodStart || !periodEnd) { toast.error("Pick a period"); return; }
    const ids = inRangeTrips.map((t) => t.trip_id);
    setSelectedTripIds(ids);
    if (ids.length === 0) toast.message("No trips in this period.");
    else toast.success(`Pulled ${ids.length} trip${ids.length === 1 ? "" : "s"}`);
  };

  const toggleTrip = (id) =>
    setSelectedTripIds((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const tripLine = (t) => {
    if (!driver) return { miles: 0, gross: 0, rate: 0, amount: 0 };
    // For team trips the load's rate comes from the first-assigned driver so
    // every team member on the load gets the same pay regardless of their own
    // individual rate.
    const lead = t.is_team ? drivers.find((d) => d.driver_id === t.driver_ids[0]) : null;
    const basis = lead || driver;
    const miles = parseFloat(t.paid_miles) || 0;
    const gross = parseFloat(t.gross_amount) || 0;
    const fuel = parseFloat(t.fuel_surcharge) || 0;
    const rate = basis.pay_rate;
    if (basis.pay_type === "per_mile") {
      return { miles, gross: 0, rate, amount: miles * rate };
    }
    // Percentage pay (matches user's revised pen math 2026-10-08):
    //   line_haul   = miles × base_rate
    //   after_bo    = line_haul × (100 - back_office%)/100
    //   fuel        = miles × fuel_rate (or flat fallback)
    //   total_rev   = after_bo + fuel
    //   driver_cut  = total_rev × driver_pct / 100
    //   if team:    driver_cut / 2
    // Back office % and driver's % are INDEPENDENT (not complements).
    const brOverride = parseFloat(payRunBaseRate);
    const baseRate = (!Number.isNaN(brOverride) && brOverride > 0)
      ? brOverride
      : (parseFloat(basis.base_rate_per_mile) || 0);
    if (baseRate > 0) {
      const lineHaul = miles * baseRate;
      const boOverride = parseFloat(payRunBoPct);
      const bo = !Number.isNaN(boOverride) ? boOverride
               : (t.back_office_percent != null ? Number(t.back_office_percent)
               : (basis.default_back_office_percent != null ? Number(basis.default_back_office_percent) : 35));
      const dpctOverride = parseFloat(payRunDriverPct);
      const driverPct = (!Number.isNaN(dpctOverride) && dpctOverride > 0) ? dpctOverride : Number(basis.pay_rate);
      const afterBo = lineHaul * Math.max(0, (100 - bo) / 100);
      const fsr = parseFloat(t.fuel_surcharge_rate) || 0;
      const fuelDollars = fsr > 0 ? miles * fsr : (parseFloat(t.fuel_surcharge) || 0);
      const totalRev = afterBo + fuelDollars;
      let driverCut = totalRev * (driverPct / 100);
      if (t.is_team) driverCut = driverCut / 2;
      return { miles, gross: lineHaul, rate: driverPct, fuel: fuelDollars, amount: driverCut, baseRate };
    }
    // Legacy fallback (no base_rate configured): % of trip.gross_amount
    return { miles: 0, gross, rate, amount: gross * (rate / 100) };
  };

  const selectedTrips = inRangeTrips.filter((t) => selectedTripIds.includes(t.trip_id));
  const tripDeductionsTotal = selectedTrips.reduce((sum, t) => {
    (t.deductions || []).forEach((d) => {
      if (d.kind === "deduction") {
        const pct = d.percent != null ? Number(d.percent) : 100;
        sum += (parseFloat(d.amount) || 0) * pct / 100;
      }
    });
    return sum;
  }, 0);
  const tripReimbTotal = selectedTrips.reduce((sum, t) => {
    (t.deductions || []).forEach((d) => {
      if (d.kind === "reimbursement") {
        const pct = d.percent != null ? Number(d.percent) : 100;
        sum += (parseFloat(d.amount) || 0) * pct / 100;
      }
    });
    return sum;
  }, 0);
  const tripGrossPay = selectedTrips.reduce((sum, t) => sum + tripLine(t).amount, 0);

  const extraDed = extraLines.filter((x) => x.kind === "deduction").reduce((s, x) => s + (parseFloat(x.amount) || 0), 0);
  const extraReimb = extraLines.filter((x) => x.kind === "reimbursement").reduce((s, x) => s + (parseFloat(x.amount) || 0), 0);
  const extraBonus = extraLines.filter((x) => x.kind === "bonus" || x.kind === "flat").reduce((s, x) => s + (parseFloat(x.amount) || 0), 0);

  // Weekly recurring items -- applied ONCE regardless of trip count.
  const activeWeekly = weeklyLines.filter((w) => w.enabled && (parseFloat(w.amount) || 0) > 0 && (w.label || "").trim() !== "");
  const weeklyDedTotal = activeWeekly.filter((w) => w.kind === "deduction").reduce((s, w) => s + (parseFloat(w.amount) || 0), 0);
  const weeklyReimbTotal = activeWeekly.filter((w) => w.kind === "reimbursement").reduce((s, w) => s + (parseFloat(w.amount) || 0), 0);

  const grossTotal = tripGrossPay + extraBonus;
  const dedTotal = tripDeductionsTotal + extraDed + weeklyDedTotal;
  const reimbTotal = tripReimbTotal + extraReimb + weeklyReimbTotal;
  const netTotal = grossTotal - dedTotal + reimbTotal;
  const totalMiles = selectedTrips.reduce((s, t) => s + (parseFloat(t.paid_miles) || 0), 0);

  const addLine = (kind) =>
    setExtraLines((p) => [...p, { _rid: crypto.randomUUID(), label: "", amount: "", kind }]);
  const updLine = (i, k, v) =>
    setExtraLines((p) => { const n = [...p]; n[i] = { ...n[i], [k]: k === "amount" ? parseFloat(v) || 0 : v }; return n; });
  const rmLine = (i) => setExtraLines((p) => p.filter((_, idx) => idx !== i));

  const savePayRun = async () => {
    if (!driverId || !payDate || !periodStart || !periodEnd || selectedTripIds.length === 0) {
      toast.error("Driver, pay date, period and at least one trip required"); return;
    }
    try {
      const r = await api.post("/pay-statements", {
        driver_id: driverId,
        pay_date: payDate,
        period_start: periodStart,
        period_end: periodEnd,
        trip_ids: selectedTripIds,
        pay_run_driver_pct: (driver && driver.pay_type === "per_percentage" && payRunDriverPct !== "")
          ? (parseFloat(payRunDriverPct) || null) : null,
        pay_run_back_office_pct: (driver && driver.pay_type === "per_percentage" && payRunBoPct !== "" && !Number.isNaN(parseFloat(payRunBoPct)))
          ? parseFloat(payRunBoPct) : null,
        pay_run_base_rate_per_mile: (driver && driver.pay_type === "per_percentage" && payRunBaseRate !== "" && !Number.isNaN(parseFloat(payRunBaseRate)) && parseFloat(payRunBaseRate) > 0)
          ? parseFloat(payRunBaseRate) : null,
        weekly_deductions: activeWeekly.map((w) => ({
          label: w.label, amount: parseFloat(w.amount) || 0, kind: w.kind, note: w.note || "",
        })),
        extra_adjustments: extraLines.map((x) => ({ label: x.label, amount: parseFloat(x.amount) || 0, kind: x.kind === "bonus" || x.kind === "flat" ? "reimbursement" : x.kind })),
        message: message.trim() || null,
        message_audience: messageAudience,
      });
      toast.success(`Pay run saved as ${r.data.statement_number}`);
      nav(`/pay-statements/${r.data.statement_id}`);
    } catch (e) { toast.error(errMsg(e, "Save failed")); }
  };

  const [batching, setBatching] = useState(false);

  const batchDraft = async () => {
    if (!payDate || !periodStart || !periodEnd) {
      toast.error("Pick a pay date and period first");
      return;
    }
    if (!window.confirm(
      `Draft a pay statement for EVERY active driver with unpaid trips between ${periodStart} and ${periodEnd}?\n\n`
      + `Each statement is created as a draft — nothing is sent to drivers automatically.`
    )) return;
    setBatching(true);
    try {
      const r = await api.post("/pay-statements/batch", {
        pay_date: payDate,
        period_start: periodStart,
        period_end: periodEnd,
        message: message.trim() || null,
        message_audience: messageAudience,
        pay_run_driver_pct: payRunDriverPct !== "" ? (parseFloat(payRunDriverPct) || null) : null,
        pay_run_back_office_pct: payRunBoPct !== "" ? (parseFloat(payRunBoPct) || null) : null,
        pay_run_base_rate_per_mile: payRunBaseRate !== "" && parseFloat(payRunBaseRate) > 0 ? parseFloat(payRunBaseRate) : null,
      });
      const d = r.data || {};
      const created = (d.created || []).length;
      const skipped = (d.skipped_no_trips || []).length;
      const errored = (d.errors || []).length;
      if (created === 0) {
        toast.message(`No drafts created — ${skipped} drivers had no unpaid trips in this period.`);
      } else {
        toast.success(`Drafted ${created} statement${created === 1 ? "" : "s"} · ${skipped} skipped${errored ? ` · ${errored} errors` : ""}`);
        nav("/pay-statements");
      }
    } catch (e) {
      toast.error(errMsg(e, "Batch pay run failed"));
    } finally {
      setBatching(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="pay-calculator-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-2xl font-semibold text-slate-900">Pay Calculator</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Build a driver's pay period as line items, then save it as a pay statement.
            Statement # auto-generates as <strong className="font-mono">MMDDYYYY-XX</strong> from the pay date.
          </p>
        </div>
        <Button
          onClick={batchDraft}
          disabled={batching || !payDate || !periodStart || !periodEnd}
          className="bg-indigo-600 hover:bg-indigo-700 text-white disabled:bg-indigo-300"
          data-testid="batch-draft-btn"
          title={!payDate || !periodStart || !periodEnd
            ? "Set pay date and period below first"
            : "Draft one statement for every active driver with unpaid trips in this period"}
        >
          {batching ? "Drafting…" : "📋 Draft for all drivers"}
        </Button>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white p-5 space-y-5">
        <div className="eyebrow">New Pay Calculation</div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          <div>
            <Label>Driver *</Label>
            <Select value={driverId} onValueChange={setDriverId}>
              <SelectTrigger data-testid="pc-driver"><SelectValue placeholder="Pick driver" /></SelectTrigger>
              <SelectContent>
                {drivers.map((d) =>
                  <SelectItem key={d.driver_id} value={d.driver_id}>
                    {d.display_id} — {d.name}
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Pay Date <span className="text-xs text-slate-400">(appears on statement)</span></Label>
            <Input type="date" value={payDate} onChange={(e) => setPayDate(e.target.value)} data-testid="pc-pay-date" />
          </div>
          <div>
            <Label>Pay Period</Label>
            <Select value={periodPreset} onValueChange={applyPreset}>
              <SelectTrigger data-testid="pc-period-preset"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="this_week">This Week</SelectItem>
                <SelectItem value="last_week">Last Week</SelectItem>
                <SelectItem value="this_month">This Month</SelectItem>
                <SelectItem value="last_month">Last Month</SelectItem>
                <SelectItem value="custom">Custom Range</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white" onClick={pullTrips} data-testid="pc-pull-trips">
              Pull trips for this period
            </Button>
          </div>
          <div>
            <Label>Start</Label>
            <Input type="date" value={periodStart} onChange={(e) => { setPeriodStart(e.target.value); setPeriodPreset("custom"); }} data-testid="pc-start" />
          </div>
          <div>
            <Label>End</Label>
            <Input type="date" value={periodEnd} onChange={(e) => { setPeriodEnd(e.target.value); setPeriodPreset("custom"); }} data-testid="pc-end" />
          </div>
        </div>

        {driver && (
          <div className="flex items-center gap-2 flex-wrap">
            <span className={driver.pay_type === "per_mile" ? "pill-blue" : "pill-amber"}>
              {driver.pay_type === "per_mile" ? `Per Mile · $${driver.pay_rate}/mi` : `Per % · ${driver.pay_rate}%`}
            </span>
            <span className="pill-gray">{selectedTripIds.length} trip(s) selected</span>
            <span className="pill-gray">{totalMiles.toFixed(1)} miles in period</span>
          </div>
        )}

        {driver && driver.pay_type === "per_percentage" && (
          <div className="w-full max-w-full overflow-hidden rounded-md border border-amber-200 bg-amber-50 p-4" data-testid="pc-pct-override">
            <Label className="text-sm font-semibold text-slate-900">Driver's Pay Split</Label>
            <div className="text-[11px] text-slate-600 mt-1 break-words leading-relaxed">
              Two independent percentages:
              <br />1. <strong>Back office %</strong> comes out of the <strong>line haul</strong> first (fuel is NOT taxed by back office).
              <br />2. <strong>Driver's %</strong> is applied to <strong>total revenue</strong> (after-back-office + fuel). Team trips split 50/50.
              <br />Example: 1122 mi · $3.05/mi · 90% fuel · BO 35% · Driver 40% · team → <strong>$646.84 per driver</strong>.
            </div>

            <div className="mt-3 space-y-3 w-full min-w-0">
              {/* Line-haul rate */}
              <div>
                <Label className="text-[11px] text-slate-700 font-semibold block">Line haul rate ($ / mile)
                  <span className="font-normal text-slate-500"> · remembered until you change it</span>
                </Label>
                <Input
                  type="number" step="0.01" inputMode="decimal" size={1}
                  className="h-11 w-full min-w-0 text-right font-mono text-base border-amber-400 focus:border-amber-600 mt-1"
                  data-testid="pc-base-rate-input"
                  value={payRunBaseRate}
                  onChange={(e) => setPayRunBaseRate(e.target.value)}
                  placeholder={String((driver && driver.base_rate_per_mile) || "e.g. 3.05")}
                />
              </div>

              {/* Back office % (independent) */}
              <div>
                <Label className="text-[11px] text-slate-700 font-semibold block">Back office % (of line haul only)
                  <span className="font-normal text-slate-500"> · typical 35</span>
                </Label>
                <Input
                  type="number" step="0.1" inputMode="decimal" size={1}
                  className="h-11 w-full min-w-0 text-right font-mono text-base border-amber-400 focus:border-amber-600 mt-1"
                  data-testid="pc-bo-input"
                  value={payRunBoPct}
                  onChange={(e) => setPayRunBoPct(e.target.value)}
                  placeholder="35"
                />
              </div>

              {/* Driver's % (independent) */}
              <div>
                <Label className="text-[11px] text-slate-700 font-semibold block">Driver's % (of total revenue after BO + fuel)
                  <span className="font-normal text-slate-500"> · typical 40</span>
                </Label>
                <Input
                  type="number" step="0.1" inputMode="decimal" size={1}
                  className="h-11 w-full min-w-0 text-right font-mono text-base border-amber-400 focus:border-amber-600 mt-1"
                  data-testid="pc-pct-input"
                  value={payRunDriverPct}
                  onChange={(e) => setPayRunDriverPct(e.target.value)}
                  placeholder="40"
                />
                <div className="text-[11px] text-slate-500 mt-1">
                  Company keeps the other {Math.max(0, 100 - (parseFloat(payRunDriverPct) || 0)).toFixed(0)}% of total revenue.
                </div>
              </div>

              <Button
                type="button"
                variant="outline"
                className="w-full h-10 border-amber-500 text-amber-800 hover:bg-amber-100 whitespace-normal"
                onClick={applyAsCompanyDefault}
                disabled={applyingDefault}
                data-testid="pc-apply-default-btn"
              >
                {applyingDefault ? "Saving…" : "Save as company default (all percentage drivers)"}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Pay line items */}
      <div className="rounded-lg border border-slate-200 bg-white">
        <div className="px-5 py-3 border-b border-slate-100">
          <div className="eyebrow">Pay Line Items</div>
          <div className="text-xs text-slate-500 mt-0.5">
            Trip pay + extra adjustments. Deductions reduce the total, reimbursements and bonuses add to it.
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500 bg-slate-50">
                <th className="px-4 py-2.5 w-28">Kind</th>
                <th className="px-4 py-2.5">Label</th>
                <th className="px-4 py-2.5 text-right">Quantity</th>
                <th className="px-4 py-2.5 text-right">{driver?.pay_type === "per_mile" ? "Rate" : "Driver %"}</th>
                <th className="px-4 py-2.5 text-right">Amount</th>
                <th className="px-4 py-2.5 w-10"></th>
              </tr>
            </thead>
            <tbody>
              {selectedTripIds.length === 0 && extraLines.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center">
                    <div className="font-semibold text-slate-700">No line items yet</div>
                    <div className="text-xs text-slate-500 mt-1">
                      Pull trips for the period or add lines by hand — per mile, percentage, flat, deduction, reimbursement, bonus.
                    </div>
                  </td>
                </tr>
              )}
              {selectedTrips.map((t) => {
                const l = tripLine(t);
                return (
                  <tr key={t.trip_id} className="border-t border-slate-100">
                    <td className="px-4 py-2 align-top"><span className={driver?.pay_type === "per_mile" ? "pill-blue" : "pill-amber"}>Trip pay</span></td>
                    <td className="px-4 py-2 align-top">
                      <div className="font-mono text-xs font-semibold">{t.trip_number} <span className="text-slate-400 font-sans">· {t.load_date}</span>
                        {t.is_team && <span className="pill-amber ml-1">Team</span>}
                      </div>
                      <div className="text-xs text-slate-500">{t.origin_city}, {t.origin_state} → {t.destination_city}, {t.destination_state}</div>
                      {(t.deductions || []).map((d, i) => {
                        const pct = d.percent != null ? Number(d.percent) : 100;
                        const applied = (parseFloat(d.amount) || 0) * pct / 100;
                        return (
                          <div key={d._rid || `pd-${t.trip_id}-${i}`} className={`text-[11px] ${d.kind === "deduction" ? "text-rose-600" : "text-emerald-600"}`}>
                            {d.kind === "deduction" ? "−" : "+"} {d.label} {d.note && `(${d.note})`}
                            {pct !== 100 && ` — $${(parseFloat(d.amount) || 0).toFixed(2)} × ${pct}%`}
                            {" "}= ${applied.toFixed(2)}
                          </div>
                        );
                      })}
                    </td>
                    <td className="px-4 py-2 text-right font-mono align-top">{driver?.pay_type === "per_mile" ? l.miles.toFixed(1) : `$${l.gross.toFixed(2)}`}</td>
                    <td className="px-4 py-2 text-right font-mono align-top">{driver?.pay_type === "per_mile" ? `$${l.rate.toFixed(2)}` : `${l.rate.toFixed(2)}%`}</td>
                    <td className="px-4 py-2 text-right font-mono font-semibold align-top">${l.amount.toFixed(2)}</td>
                    <td className="px-4 py-2 align-top">
                      <Button size="icon" variant="ghost" onClick={() => toggleTrip(t.trip_id)}>
                        <Trash2 size={14} className="text-rose-600" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
              {extraLines.map((x, i) => (
                <tr key={`x-${i}`} className="border-t border-slate-100">
                  <td className="px-4 py-2">
                    <Select value={x.kind} onValueChange={(v) => updLine(i, "kind", v)}>
                      <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="flat">Flat</SelectItem>
                        <SelectItem value="bonus">Bonus</SelectItem>
                        <SelectItem value="deduction">Deduction</SelectItem>
                        <SelectItem value="reimbursement">Reimbursement</SelectItem>
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-4 py-2">
                    <Input className="h-8" placeholder="Label" value={x.label} onChange={(e) => updLine(i, "label", e.target.value)} />
                  </td>
                  <td className="px-4 py-2 text-right text-xs text-slate-400">—</td>
                  <td className="px-4 py-2 text-right text-xs text-slate-400">—</td>
                  <td className="px-4 py-2 text-right">
                    <Input className="h-8 text-right font-mono" type="number" step="0.01" value={x.amount} onChange={(e) => updLine(i, "amount", e.target.value)} />
                  </td>
                  <td className="px-4 py-2">
                    <Button size="icon" variant="ghost" onClick={() => rmLine(i)}>
                      <Trash2 size={14} className="text-rose-600" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="px-4 py-3 border-t border-slate-100 flex items-center gap-2 flex-wrap">
          <Button size="sm" variant="outline" onClick={() => addLine("flat")}>+ Add line</Button>
          <Button size="sm" variant="outline" onClick={() => addLine("deduction")}>+ Deduction</Button>
          <Button size="sm" variant="outline" onClick={() => addLine("reimbursement")}>+ Reimbursement</Button>
          <Button size="sm" variant="outline" onClick={() => addLine("bonus")}>+ Bonus</Button>
          <span className="text-xs text-slate-400 ml-auto">Enter on any cell adds a row</span>
        </div>
      </div>

      {/* Weekly deductions -- applied ONCE per statement regardless of trip count */}
      <div className="rounded-lg border border-slate-200 bg-white" data-testid="weekly-deductions-card">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="font-semibold text-sm text-slate-900">Weekly Deductions / Reimbursements</div>
            <div className="text-xs text-slate-500">
              Applied <strong>once per pay statement</strong> — never per trip. Pulled from the driver's profile; edit for this period only.
            </div>
          </div>
          <Button size="sm" variant="outline" onClick={addWeekly} data-testid="pc-add-weekly-btn">
            <Plus size={14} className="mr-1" /> Add weekly item
          </Button>
        </div>
        <div className="divide-y">
          {weeklyLines.length === 0 && (
            <div className="px-5 py-4 text-xs text-slate-400 italic">
              {driverId ? "This driver has no weekly items. Add them here (one-off) or on the driver profile (recurring)."
                        : "Pick a driver to load their weekly items."}
            </div>
          )}
          {weeklyLines.map((w, i) => (
            <div key={w._rid || `pw-${i}`} className="grid grid-cols-12 gap-3 px-5 py-2.5 items-center" data-testid={`pc-weekly-row-${i}`}>
              <div className="col-span-1">
                <input type="checkbox" checked={w.enabled !== false}
                       onChange={(e) => updWeekly(i, "enabled", e.target.checked)}
                       data-testid={`pc-weekly-enable-${i}`}
                       title="Include on this statement" />
              </div>
              <div className="col-span-4">
                <Input className="h-8" placeholder="Label" value={w.label}
                       onChange={(e) => updWeekly(i, "label", e.target.value)}
                       data-testid={`pc-weekly-label-${i}`} />
              </div>
              <div className="col-span-3">
                <Select value={w.kind} onValueChange={(v) => updWeekly(i, "kind", v)}>
                  <SelectTrigger className="h-8 text-xs" data-testid={`pc-weekly-kind-${i}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="deduction">Deduction</SelectItem>
                    <SelectItem value="reimbursement">Reimbursement</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-3">
                <Input className="h-8 text-right font-mono" type="number" step="0.01" value={w.amount}
                       onChange={(e) => updWeekly(i, "amount", e.target.value)}
                       data-testid={`pc-weekly-amount-${i}`} />
              </div>
              <div className="col-span-1 text-right">
                <Button size="icon" variant="ghost" onClick={() => rmWeekly(i)} data-testid={`pc-weekly-remove-${i}`}>
                  <Trash2 size={14} className="text-rose-600" />
                </Button>
              </div>
            </div>
          ))}
        </div>
        {weeklyLines.length > 0 && (
          <div className="px-5 py-2.5 border-t border-slate-100 text-xs text-slate-500 flex items-center justify-between">
            <span>{activeWeekly.length} active · applied once on this statement</span>
            <span className="font-mono">
              <span className="text-rose-600">−${weeklyDedTotal.toFixed(2)}</span>
              {" / "}
              <span className="text-emerald-600">+${weeklyReimbTotal.toFixed(2)}</span>
            </span>
          </div>
        )}
      </div>

      {/* Pay run notes */}
      <div className="rounded-lg border border-slate-200 bg-white p-5">
        <div className="eyebrow mb-2">Pay Run Notes <span className="text-slate-400 font-normal normal-case tracking-normal text-xs">(internal — not shown on statement)</span></div>
        <Textarea rows={2} placeholder="Settlement notes, adjustments agreed with the driver..." value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="pc-notes" />
      </div>

      {/* Dispatcher message that prints on the pay statement */}
      <div className="rounded-lg border-2 border-amber-300 bg-amber-50 p-5" data-testid="pc-message-card">
        <div className="eyebrow text-amber-900 mb-1">Message to driver on pay statement</div>
        <div className="text-xs text-slate-600 mb-3">
          Prints on this statement (on-screen + PDF). Pick who it's for below — paste the same text in every pay run this week for a fleet-wide announcement.
        </div>
        <div className="flex gap-2 flex-wrap mb-3" role="radiogroup" aria-label="Who the message is for" data-testid="pc-message-audience">
          {[
            { v: "driver", label: "This driver only", hint: "Prints only on this statement" },
            { v: "team",   label: "Team drivers only", hint: "Hides on single-driver statements" },
            { v: "all",    label: "All drivers", hint: "Marked as fleet-wide announcement" },
          ].map((opt) => (
            <button
              key={opt.v}
              type="button"
              role="radio"
              aria-checked={messageAudience === opt.v}
              onClick={() => setMessageAudience(opt.v)}
              data-testid={`pc-audience-${opt.v}`}
              className={
                "flex-1 min-w-[160px] text-left px-3 py-2 rounded border transition " +
                (messageAudience === opt.v
                  ? "border-amber-600 bg-white ring-2 ring-amber-400"
                  : "border-amber-200 bg-white/60 hover:border-amber-400")
              }
            >
              <div className="text-sm font-semibold text-slate-900">{opt.label}</div>
              <div className="text-[11px] text-slate-500">{opt.hint}</div>
            </button>
          ))}
        </div>
        <Textarea
          rows={3}
          placeholder="e.g. Reminder: all drivers must turn in receipts by Monday. Happy Thanksgiving — the office is closed Thursday."
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          data-testid="pc-message"
        />
      </div>

      {/* Totals bar */}
      <div className="rounded-lg border border-slate-200 bg-white p-5">
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <div><div className="eyebrow">Miles</div><div className="font-mono font-semibold text-lg">{totalMiles.toFixed(1)}</div></div>
          <div><div className="eyebrow">Gross Pay</div><div className="font-mono font-semibold text-lg">${grossTotal.toFixed(2)}</div></div>
          <div><div className="eyebrow">Total Deductions</div><div className="font-mono font-semibold text-lg text-rose-600">−${dedTotal.toFixed(2)}</div></div>
          <div><div className="eyebrow">Total Reimbursements</div><div className="font-mono font-semibold text-lg text-emerald-600">+${reimbTotal.toFixed(2)}</div></div>
          <div className="bg-blue-50 rounded px-3 py-2"><div className="eyebrow">Net Pay</div><div className="font-mono font-bold text-xl text-blue-700">${netTotal.toFixed(2)}</div></div>
        </div>
        <div className="mt-4 flex items-center justify-between flex-wrap gap-3">
          <div className="text-xs text-slate-500">
            Net pay = gross pay − deductions + reimbursements. {selectedTripIds.length + extraLines.length} line item(s).
          </div>
          <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={savePayRun} data-testid="save-pay-run">
            <FileText size={14} className="mr-1" /> Save pay run
          </Button>
        </div>
      </div>
    </div>
  );
}
